/**
 * Administration → Access → Single sign-on (1.0.0-rc.4): OpenID Connect identity providers (Entra ID, Okta, Google,
 * Keycloak, …), the redirect URI to register with them, Test configuration, the mapping of IdP groups to roles and
 * access groups, and the mode: passwords only (the default), both, or single sign-on required for chosen email
 * domains, with a break-glass local administrator that the domain rule never locks out.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Copy, ExternalLink, FlaskConical, Loader2, LogIn, Pencil, Plus, Trash2, Waypoints } from 'lucide-react';
import { api, errorMessage } from '../../api/client';
import { useAccessGroups, useRoles } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { copy } from '../../lib/clipboard';
import { relative } from '../../lib/format';

interface Mapping { idpGroup: string; role: string | null; accessGroupId: string | null }
interface TestResult {
  at: string; ok: boolean; kind: 'discovery' | 'sign-in';
  checks?: Array<{ name: string; ok: boolean; detail?: string }>;
  claims?: { sub: string; email: string | null; emailVerified: unknown; name: string | null; groups: string[] };
  role?: string; accessGroups?: string[]; problem?: string | null;
}
interface Provider {
  id: string; name: string; issuer: string; clientId: string; hasClientSecret: boolean; scopes: string; groupsClaim: string; trustEmail: boolean;
  defaultRole: string; createAccounts: boolean; enabled: boolean; lastTest: TestResult | null; mappings: Mapping[];
}
interface SsoDTO {
  mode: 'local' | 'both' | 'domains'; domains: string[]; breakGlassUserId: string | null; redirectUri: string;
  breakGlass: { id: string; email: string; displayName: string } | null; breakGlassCandidates: Array<{ id: string; email: string; displayName: string }>;
  providers: Provider[];
}

const MODES = [
  { value: 'local', label: 'Passwords only', help: 'No single sign-on buttons on the sign-in page (the default).' },
  { value: 'both', label: 'Single sign-on and passwords', help: 'The sign-in page offers each enabled provider and the password form.' },
  { value: 'domains', label: 'Single sign-on required for chosen email domains', help: 'People with an address in these domains cannot use a PRISM password; the break-glass administrator always can.' },
] as const;

export function SsoAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sso'], queryFn: () => api<SsoDTO>('/api/v1/sso') });
  const [params, setParams] = useSearchParams();
  const ref = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState<Provider | 'new' | null>(null);
  const [mapping, setMapping] = useState<Provider | null>(null);
  const [testing, setTesting] = useState<{ provider: Provider; result: TestResult & { signInUrl?: string } } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [mode, setMode] = useState<SsoDTO['mode']>('local');
  const [domains, setDomains] = useState('');
  const [breakGlass, setBreakGlass] = useState('');
  const d = q.data;
  useEffect(() => {
    if (!d) return;
    setMode(d.mode);
    setDomains(d.domains.join(', '));
    setBreakGlass(d.breakGlassUserId ?? '');
  }, [d]);
  // From the setup wizard, or back from a test sign-in at the provider (?sso-test=<id>).
  const back = params.get('sso-test');
  useEffect(() => {
    if (params.get('view') === 'sso' || back) ref.current?.scrollIntoView({ block: 'start' });
    if (back && d) {
      const p = d.providers.find((x) => x.id === back);
      if (p?.lastTest) setTesting({ provider: p, result: p.lastTest });
      params.delete('sso-test');
      setParams(params, { replace: true });
    }
  }, [back, d, params, setParams]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['sso'] });

  async function saveMode() {
    setBusy('mode');
    try {
      await api('/api/v1/sso/settings', { method: 'PUT', json: { mode, domains: domains.split(/[\s,]+/).filter(Boolean), breakGlassUserId: breakGlass || null } });
      toast('Single sign-on settings saved');
      await refresh();
      void qc.invalidateQueries({ queryKey: ['auth-hints'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  async function test(p: Provider) {
    setBusy(p.id);
    try {
      setTesting({ provider: p, result: await api(`/api/v1/sso/providers/${p.id}/test`, { method: 'POST', json: {} }) });
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }
  async function remove(p: Provider) {
    if (!window.confirm(`Remove “${p.name}”? People signed in through it are signed out; their PRISM accounts stay.`)) return;
    try {
      await api(`/api/v1/sso/providers/${p.id}`, { method: 'DELETE' });
      toast(`Removed ${p.name}`);
      await refresh();
      void qc.invalidateQueries({ queryKey: ['auth-hints'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <section className="panel-card" style={{ marginBottom: 20 }} ref={ref} data-testid="sso-admin">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ flex: 1, margin: 0 }}><Waypoints size={16} color="var(--accent-strong)" /> Single sign-on</h3>
        <button className="btn sm" onClick={() => setEditing('new')} data-testid="sso-add-provider"><Plus size={14} /> Add identity provider</button>
      </div>
      <p className="muted" style={{ margin: 0 }}>Sign in with your organisation’s accounts through OpenID Connect: Microsoft Entra ID, Okta, Google Workspace, Keycloak and others.
        Accounts are made at the first sign-in and matched by verified email; the IdP’s groups can set the role and access groups. docs/deployment.md has the steps for each provider.</p>
      {d && (
        <div className="field"><label>Redirect URI to register at the provider</label>
          <div style={{ display: 'flex', gap: 8 }}><code className="code-block" style={{ flex: 1 }} data-testid="sso-redirect-uri">{d.redirectUri}</code>
            <button className="btn icon" aria-label="Copy redirect URI" onClick={() => copy(d.redirectUri)}><Copy size={14} /></button></div>
          <span className="help">PRISM_PUBLIC_URL followed by /api/v1/auth/sso/callback; the same for every provider.</span></div>
      )}
      {d && d.providers.length > 0 && (
        <TableScroll>
        <table className="table compact" data-testid="sso-providers">
          <thead><tr><th>Provider</th><th>Client</th><th>Groups</th><th>Last test</th><th /></tr></thead>
          <tbody>{d.providers.map((p) => (
            <tr key={p.id} data-provider={p.name} className={p.enabled ? undefined : 'faint'}>
              <td className="wrap" style={{ minWidth: 180 }}><div style={{ fontWeight: 560 }}>{p.name}{!p.enabled && <span className="pill" style={{ marginLeft: 6 }}>Disabled</span>}</div><div className="faint mono" style={{ fontSize: 11.5 }}>{p.issuer}</div></td>
              <td className="muted wrap"><span className="mono" style={{ fontSize: 12 }}>{p.clientId}</span><div className="faint" style={{ fontSize: 11.5 }}>{p.hasClientSecret ? 'Secret stored (encrypted)' : 'Public client (PKCE)'}</div></td>
              <td className="muted wrap">{p.mappings.length ? `${p.mappings.length} mapped` : 'Not mapped'} <span className="faint">· claim {p.groupsClaim}</span></td>
              <td className="muted wrap">{p.lastTest ? <span className={`pill ${p.lastTest.ok ? 'accent' : 'danger'}`}>{p.lastTest.ok ? 'OK' : 'Problem'}</span> : 'Never'} {p.lastTest && relative(p.lastTest.at)}</td>
              <td className="actions">
                <button className="btn ghost sm" disabled={busy === p.id} onClick={() => void test(p)} data-testid="sso-test">{busy === p.id ? <Loader2 size={13} className="spin" /> : <FlaskConical size={13} />} Test configuration</button>
                <button className="btn ghost sm" onClick={() => setMapping(p)} data-testid="sso-mappings">Group mapping</button>
                <button className="btn ghost sm" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p)}><Pencil size={13} /></button>
                <button className="btn ghost sm danger" aria-label={`Remove ${p.name}`} onClick={() => void remove(p)}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        </TableScroll>
      )}
      {d && (
        <div className="sso-mode">
          <div className="field"><label htmlFor="sso-mode">Sign-in</label>
            <select id="sso-mode" className="select" value={mode} onChange={(e) => setMode(e.target.value as SsoDTO['mode'])} data-testid="sso-mode" disabled={!d.providers.length && mode === 'local'}>
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <span className="help">{MODES.find((m) => m.value === mode)!.help}{!d.providers.length && ' Add an identity provider first.'}</span></div>
          {mode === 'domains' && (
            <div className="field"><label htmlFor="sso-domains">Email domains that must use single sign-on</label>
              <input id="sso-domains" className="input" placeholder="example.com, studio.example.com" value={domains} onChange={(e) => setDomains(e.target.value)} data-testid="sso-domains" /></div>
          )}
          {mode !== 'local' && (
            <div className="field"><label htmlFor="sso-break-glass">Break-glass administrator</label>
              <select id="sso-break-glass" className="select" value={breakGlass} onChange={(e) => setBreakGlass(e.target.value)} data-testid="sso-break-glass">
                <option value="">Automatic{d.breakGlass && !d.breakGlassUserId ? ` (${d.breakGlass.displayName}, ${d.breakGlass.email})` : ''}</option>
                {d.breakGlassCandidates.map((c) => <option key={c.id} value={c.id}>{c.displayName} ({c.email})</option>)}
              </select>
              <span className="help">A local administrator with a PRISM password who can always sign in, even if the identity provider is down or the domain rule would apply. Keep its password somewhere safe.</span></div>
          )}
          {mode !== 'local' && !d.breakGlass && <div className="banner warning"><CircleAlert size={15} /><div className="banner-body"><span>There is no local administrator with a password. Add one before requiring single sign-on.</span></div></div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn primary sm" disabled={busy === 'mode'} onClick={() => void saveMode()} data-testid="sso-save-mode">{busy === 'mode' && <Loader2 size={13} className="spin" />} Save sign-in settings</button>
          </div>
        </div>
      )}
      {editing && <ProviderDialog provider={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); }} />}
      {mapping && <MappingDialog provider={mapping} onClose={() => setMapping(null)} onSaved={() => { setMapping(null); void refresh(); }} />}
      {testing && <TestDialog {...testing} onClose={() => setTesting(null)} />}
    </section>
  );
}

function ProviderDialog({ provider, onClose, onSaved }: { provider: Provider | null; onClose: () => void; onSaved: () => void }) {
  const roles = useRoles().data?.items ?? [];
  const [f, setF] = useState({
    name: provider?.name ?? '', issuer: provider?.issuer ?? 'https://', clientId: provider?.clientId ?? '', clientSecret: '', scopes: provider?.scopes ?? 'openid email profile',
    groupsClaim: provider?.groupsClaim ?? 'groups', trustEmail: provider?.trustEmail ?? false, defaultRole: provider?.defaultRole ?? 'viewer',
    createAccounts: provider?.createAccounts ?? true, enabled: provider?.enabled ?? true,
  });
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const { clientSecret, ...rest } = f;
      const body = { ...rest, ...(clientSecret ? { clientSecret } : {}) };
      await api(provider ? `/api/v1/sso/providers/${provider.id}` : '/api/v1/sso/providers', { method: provider ? 'PATCH' : 'POST', json: body });
      toast(provider ? `Saved ${f.name}` : `Added ${f.name}`);
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={provider ? `Edit ${provider.name}` : 'Add identity provider'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !f.name.trim() || !/^https?:\/\/./.test(f.issuer) || !f.clientId.trim()} onClick={() => void save()} data-testid="sso-save-provider">Save</button>
    </>}>
      <div className="field-row">
        <div className="field"><label htmlFor="sso-name">Name on the sign-in button</label><input id="sso-name" className="input" placeholder="Microsoft" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="field"><label htmlFor="sso-default-role">Role of new accounts</label>
          <select id="sso-default-role" className="select" value={f.defaultRole} onChange={(e) => setF({ ...f, defaultRole: e.target.value })}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select><span className="help">When none of their groups is mapped to a role.</span></div>
      </div>
      <div className="field"><label htmlFor="sso-issuer">Issuer URL</label><input id="sso-issuer" className="input mono" value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} />
        <span className="help">Entra ID: https://login.microsoftonline.com/&lt;tenant id&gt;/v2.0 · Okta: https://&lt;org&gt;.okta.com · Google: https://accounts.google.com · Keycloak: https://&lt;host&gt;/realms/&lt;realm&gt;</span></div>
      <div className="field-row">
        <div className="field"><label htmlFor="sso-client">Client ID</label><input id="sso-client" className="input mono" value={f.clientId} onChange={(e) => setF({ ...f, clientId: e.target.value })} /></div>
        <div className="field"><label htmlFor="sso-secret">Client secret</label><input id="sso-secret" className="input mono" type="password" autoComplete="off" placeholder={provider?.hasClientSecret ? 'Stored; leave empty to keep it' : ''} value={f.clientSecret} onChange={(e) => setF({ ...f, clientSecret: e.target.value })} />
          <span className="help">Stored encrypted with PRISM_SECRETS_KEY and never shown again.</span></div>
      </div>
      <div className="field-row">
        <div className="field"><label htmlFor="sso-scopes">Scopes</label><input id="sso-scopes" className="input mono" value={f.scopes} onChange={(e) => setF({ ...f, scopes: e.target.value })} /></div>
        <div className="field"><label htmlFor="sso-claim">Groups claim</label><input id="sso-claim" className="input mono" value={f.groupsClaim} onChange={(e) => setF({ ...f, groupsClaim: e.target.value })} />
          <span className="help">The claim that lists the person’s groups, for example groups or roles.</span></div>
      </div>
      <label className="toggle-row"><input type="checkbox" checked={f.trustEmail} onChange={(e) => setF({ ...f, trustEmail: e.target.checked })} />
        <span><b>Trust the email claim without email_verified</b><br /><span className="faint" style={{ fontSize: 12 }}>For Entra ID, which sends no email_verified: only when the tenant’s addresses are its own.</span></span></label>
      <label className="toggle-row"><input type="checkbox" checked={f.createAccounts} onChange={(e) => setF({ ...f, createAccounts: e.target.checked })} /><span>Create a PRISM account at a person’s first sign-in</span></label>
      <label className="toggle-row"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} /><span>Enabled</span></label>
    </Modal>
  );
}

function MappingDialog({ provider, onClose, onSaved }: { provider: Provider; onClose: () => void; onSaved: () => void }) {
  const roles = useRoles().data?.items ?? [];
  const groups = useAccessGroups().data?.items ?? [];
  const [rows, setRows] = useState<Mapping[]>(provider.mappings.length ? provider.mappings : [{ idpGroup: '', role: null, accessGroupId: null }]);
  const [busy, setBusy] = useState(false);
  const set = (i: number, patch: Partial<Mapping>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  async function save() {
    setBusy(true);
    try {
      await api(`/api/v1/sso/providers/${provider.id}/mappings`, { method: 'PUT', json: { items: rows.filter((r) => r.idpGroup.trim() && (r.role || r.accessGroupId)) } });
      toast('Group mapping saved');
      onSaved();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={`Group mapping · ${provider.name}`} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy} onClick={() => void save()} data-testid="sso-save-mappings">Save</button>
    </>}>
      <p className="muted" style={{ marginTop: 0 }}>At every sign-in, the groups in the <span className="mono">{provider.groupsClaim}</span> claim set the person’s role (with several, the one with most permissions) and their membership of the access groups named here. Without any role rows, roles are left as administrators set them.</p>
      <table className="table compact" data-testid="sso-mapping-table">
        <thead><tr><th>IdP group (name or id)</th><th>Role</th><th>Access group</th><th /></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>
            <td><input className="input mono" aria-label="IdP group" value={r.idpGroup} onChange={(e) => set(i, { idpGroup: e.target.value })} placeholder="prism-editors" /></td>
            <td><select className="select" aria-label="Role" value={r.role ?? ''} onChange={(e) => set(i, { role: e.target.value || null })}>
              <option value="">—</option>{roles.map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}</select></td>
            <td><select className="select" aria-label="Access group" value={r.accessGroupId ?? ''} onChange={(e) => set(i, { accessGroupId: e.target.value || null })}>
              <option value="">—</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select></td>
            <td><button className="btn ghost icon sm" aria-label="Remove row" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={12} /></button></td>
          </tr>
        ))}</tbody>
      </table>
      <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setRows([...rows, { idpGroup: '', role: null, accessGroupId: null }])}><Plus size={13} /> Row</button>
    </Modal>
  );
}

function TestDialog({ provider, result, onClose }: { provider: Provider; result: TestResult & { signInUrl?: string }; onClose: () => void }) {
  return (
    <Modal title={`Test · ${provider.name}`} wide onClose={onClose} actions={<>
      {result.signInUrl && <a className="btn" href={result.signInUrl} data-testid="sso-test-sign-in"><LogIn size={14} /> Try a sign-in <ExternalLink size={12} /></a>}
      <button className="btn primary" onClick={onClose}>Done</button>
    </>}>
      <div className={`banner ${result.ok ? 'info' : 'error'}`}><div className="banner-body"><strong>{result.ok ? 'The configuration works' : 'Something needs attention'}</strong>
        <span>{result.kind === 'sign-in' ? 'A test sign-in at the provider (nobody was signed in to PRISM).' : 'The provider’s discovery document, endpoints and signing keys.'}</span></div></div>
      {result.checks && <ul className="check-results" data-testid="sso-test-checks">{result.checks.map((c) => <li key={c.name} className={c.ok ? 'ok' : 'bad'}>{c.ok ? '✓' : '✗'} {c.name}{c.detail && <span className="faint"> · {c.detail}</span>}</li>)}</ul>}
      {result.claims && (
        <dl className="kv">
          <dt>Subject</dt><dd className="mono">{result.claims.sub}</dd>
          <dt>Email</dt><dd>{result.claims.email ?? '—'} {result.claims.emailVerified === true ? '(verified)' : '(not marked verified)'}</dd>
          <dt>Name</dt><dd>{result.claims.name ?? '—'}</dd>
          <dt>Groups</dt><dd>{result.claims.groups.length ? result.claims.groups.join(', ') : '—'}</dd>
          <dt>Would get</dt><dd>Role {result.role}{result.accessGroups?.length ? `, ${result.accessGroups.length} access group(s)` : ''}</dd>
        </dl>
      )}
      {result.problem && <div className="banner warning"><div className="banner-body"><span>{result.problem}</span></div></div>}
      {result.kind === 'discovery' && result.ok && <p className="muted" style={{ margin: 0 }}>“Try a sign-in” goes to the provider and back, and shows what it says about you without signing you in.</p>}
    </Modal>
  );
}
